export const TermsOfUseEn = () => (
  <div className="max-w-4xl mx-auto px-4 py-12 bg-white">
    <div className="prose prose-lg max-w-none">
      <h1 className="text-4xl font-bold mb-8 text-gray-900">Terms of Use</h1>
      <p className="text-sm text-gray-600">Last updated: February 03, 2025</p>

      <p>
        Please read these Terms of Use (&quot;Terms&quot;, &quot;Terms of Use&quot;) carefully before using the DontTab service
        (&quot;Service&quot;, &quot;we&quot;, &quot;us&quot;, or &quot;our&quot;) operated by DontTab (&quot;Company&quot;).
      </p>

      <p>
        Your access to and use of the Service is conditioned on your acceptance of and compliance with
        these Terms. These Terms apply to all visitors, users, and others who access or use the Service.
      </p>

      <p>
        By accessing or using the Service you agree to be bound by these Terms. If you disagree
        with any part of the terms, then you may not access the Service.
      </p>

      <h2 className="text-2xl font-semibold mt-12 mb-6 text-gray-800">
        1. Accounts and Registration
      </h2>
      <p>
        When you create an account with us, you must provide accurate, complete, and current
        information. Failure to do so constitutes a breach of the Terms, which may result in
        immediate termination of your account on our Service.
      </p>
      <p>
        You are responsible for safeguarding the password and for all activities that occur under
        your account. You agree not to disclose your password to any third party.
      </p>

      <h2 className="text-2xl font-semibold mt-12 mb-6 text-gray-800">
        2. Subscriptions and Payments
      </h2>
      <p>
        Some parts of the Service are billed on a subscription basis. You will be billed in
        advance on a recurring and periodic basis (&quot;Billing Cycle&quot;). Billing cycles are set on
        a monthly or annual basis, depending on the subscription plan you select.
      </p>
      <p>
        Payment processing services for users on DontTab are provided by third-party payment
        processors (e.g., Stripe) and are subject to their respective terms of service and
        privacy policies. We are not responsible for the actions or inactions of these payment processors.
      </p>

      <h2 className="text-2xl font-semibold mt-12 mb-6 text-gray-800">
        3. Refund Policy
      </h2>
      <p>
        All subscription fees are non-refundable except where required by applicable law. If you
        cancel your subscription, you will continue to have access to the paid features until the
        end of your current billing cycle, after which your account will revert to the free tier.
      </p>
      <p>
        We do not provide refunds or credits for partial subscription periods, downgrade of
        subscription plans, or unused features. In the event of a billing error, please contact
        us within 30 days of the charge, and we will investigate and, if appropriate, issue a
        correction or refund.
      </p>

      <h2 className="text-2xl font-semibold mt-12 mb-6 text-gray-800">
        4. Third-Party Services and Integrations
      </h2>
      <p>
        The Service may integrate with or contain links to third-party services and websites.
        These third-party services are not under our control, and we are not responsible for
        their content, privacy policies, or practices.
      </p>
      <p>
        Your interaction with any third-party integrations within our Service is governed by
        the respective terms and privacy policies of those third parties. We strongly advise
        you to review their terms and conditions and privacy policies.
      </p>

      <h2 className="text-2xl font-semibold mt-12 mb-6 text-gray-800">
        5. Data Collection and Use
      </h2>
      <p>
        By using our Service, you agree that we may collect and use your email address and
        other personal information as described in our Privacy Policy. You acknowledge that
        this information may be shared with third-party service providers as necessary to
        provide the Service.
      </p>

      <h2 className="text-2xl font-semibold mt-12 mb-6 text-gray-800">
        6. Intellectual Property
      </h2>
      <p>
        The Service and its original content, features, and functionality are and will remain
        the exclusive property of DontTab and its licensors. The Service is protected by
        copyright, trademark, and other laws of both the United States and foreign countries.
      </p>

      <h2 className="text-2xl font-semibold mt-12 mb-6 text-gray-800">
        7. Prohibited Uses
      </h2>
      <p>You agree not to use the Service:</p>
      <ul className="list-disc pl-6 space-y-4 mb-8">
        <li className="text-gray-700">
          In any way that violates any applicable laws or regulations
        </li>
        <li className="text-gray-700">
          To transmit any material that contains viruses or other harmful computer code
        </li>
        <li className="text-gray-700">
          To impersonate or attempt to impersonate the Company or another user
        </li>
        <li className="text-gray-700">
          To interfere with or disrupt the Service or servers or networks connected to the Service
        </li>
      </ul>

      <h2 className="text-2xl font-semibold mt-12 mb-6 text-gray-800">
        8. Termination
      </h2>
      <p>
        We may terminate or suspend your account immediately, without prior notice or liability,
        for any reason whatsoever, including without limitation if you breach the Terms.
      </p>
      <p>
        Upon termination, your right to use the Service will immediately cease. If you wish to
        terminate your account, you may simply discontinue using the Service or contact us for
        account deletion.
      </p>

      <h2 className="text-2xl font-semibold mt-12 mb-6 text-gray-800">
        9. Service Availability
      </h2>
      <p>
        We strive to keep the Service available at all times, but we do not guarantee
        uninterrupted, timely, secure, or error-free operation. The Service may contain bugs,
        errors, or inaccuracies that may affect its functionality. We are not liable for any
        loss or damage arising from service interruptions, downtime, bugs, errors, data loss,
        or other issues related to the availability or performance of the Service.
      </p>
      <p>
        We reserve the right to modify, suspend, or discontinue the Service (or any part
        thereof) at any time, with or without notice, for maintenance, updates, or any other
        reason. We shall not be liable to you or any third party for any such modification,
        suspension, or discontinuance.
      </p>

      <h2 className="text-2xl font-semibold mt-12 mb-6 text-gray-800">
        10. Disclaimer of Warranties
      </h2>
      <p>
        THE SERVICE IS PROVIDED ON AN &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; BASIS,
        WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. TO THE FULLEST EXTENT
        PERMITTED BY APPLICABLE LAW, DONTTAB DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED,
        INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
        PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT.
      </p>
      <p>
        WITHOUT LIMITING THE FOREGOING, DONTTAB DOES NOT WARRANT THAT: (A) THE SERVICE WILL
        MEET YOUR REQUIREMENTS OR EXPECTATIONS; (B) THE SERVICE WILL BE UNINTERRUPTED, TIMELY,
        SECURE, OR ERROR-FREE; (C) THE RESULTS OBTAINED FROM THE USE OF THE SERVICE WILL BE
        ACCURATE OR RELIABLE; (D) ANY ERRORS OR DEFECTS IN THE SERVICE WILL BE CORRECTED; OR
        (E) THE SERVICE OR THE SERVERS THAT MAKE THE SERVICE AVAILABLE ARE FREE OF VIRUSES OR
        OTHER HARMFUL COMPONENTS.
      </p>
      <p>
        YOU ACKNOWLEDGE THAT YOUR USE OF THE SERVICE IS AT YOUR SOLE RISK. THIS DISCLAIMER
        APPLIES TO ALL USERS, INCLUDING FREE AND PAID SUBSCRIPTION USERS. PAYMENT OF A
        SUBSCRIPTION FEE DOES NOT ENTITLE YOU TO ANY ADDITIONAL WARRANTIES BEYOND THOSE
        EXPRESSLY STATED IN THESE TERMS.
      </p>

      <h2 className="text-2xl font-semibold mt-12 mb-6 text-gray-800">
        11. Limitation of Liability
      </h2>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL DONTTAB, NOR ITS
        DIRECTORS, EMPLOYEES, PARTNERS, AGENTS, SUPPLIERS, OR AFFILIATES, BE LIABLE FOR ANY
        INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT
        LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING
        FROM: (A) YOUR ACCESS TO OR USE OF OR INABILITY TO ACCESS OR USE THE SERVICE; (B) ANY
        CONDUCT OR CONTENT OF ANY THIRD PARTY ON THE SERVICE; (C) ANY CONTENT OBTAINED FROM THE
        SERVICE; (D) UNAUTHORIZED ACCESS, USE, OR ALTERATION OF YOUR TRANSMISSIONS OR CONTENT;
        OR (E) ANY BUGS, VIRUSES, ERRORS, OR INACCURACIES IN THE SERVICE, WHETHER OR NOT CAUSED
        BY OUR NEGLIGENCE AND WHETHER OR NOT WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH
        DAMAGES.
      </p>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, THE TOTAL LIABILITY OF DONTTAB FOR
        ALL CLAIMS ARISING OUT OF OR RELATING TO THESE TERMS OR THE SERVICE SHALL NOT EXCEED THE
        GREATER OF: (A) THE AMOUNT YOU PAID TO DONTTAB FOR THE SERVICE DURING THE TWELVE (12)
        MONTHS IMMEDIATELY PRECEDING THE EVENT GIVING RISE TO THE CLAIM; OR (B) ONE HUNDRED
        U.S. DOLLARS (USD $100.00).
      </p>
      <p>
        THIS LIMITATION OF LIABILITY APPLIES TO ALL USERS, INCLUDING PAID SUBSCRIBERS, AND SHALL
        APPLY REGARDLESS OF THE LEGAL THEORY ON WHICH THE CLAIM IS BASED, INCLUDING BUT NOT
        LIMITED TO CONTRACT, TORT (INCLUDING NEGLIGENCE), STRICT LIABILITY, OR ANY OTHER THEORY.
      </p>

      <h2 className="text-2xl font-semibold mt-12 mb-6 text-gray-800">
        12. Indemnification
      </h2>
      <p>
        You agree to defend, indemnify, and hold harmless DontTab and its officers, directors,
        employees, agents, licensors, and suppliers from and against any claims, actions,
        demands, liabilities, damages, losses, costs, and expenses (including reasonable
        attorneys&apos; fees) arising out of or relating to: (a) your use of the Service;
        (b) your violation of these Terms; (c) your violation of any rights of another party;
        or (d) any content you submit, post, or transmit through the Service.
      </p>

      <h2 className="text-2xl font-semibold mt-12 mb-6 text-gray-800">
        13. Governing Law and Dispute Resolution
      </h2>
      <p>
        These Terms shall be governed by and construed in accordance with the laws of the
        Federative Republic of Brazil, without regard to its conflict of law provisions.
      </p>
      <p>
        Any dispute arising out of or relating to these Terms or the Service shall first be
        attempted to be resolved through good-faith negotiation between the parties. If the
        dispute cannot be resolved through negotiation within thirty (30) days, either party
        may pursue resolution through the competent courts of Brazil.
      </p>
      <p>
        Nothing in this section shall prevent either party from seeking injunctive or other
        equitable relief in any court of competent jurisdiction to prevent the actual or
        threatened infringement, misappropriation, or violation of intellectual property rights.
      </p>

      <h2 className="text-2xl font-semibold mt-12 mb-6 text-gray-800">
        14. Force Majeure
      </h2>
      <p>
        DontTab shall not be liable for any failure or delay in performing its obligations
        under these Terms where such failure or delay results from circumstances beyond our
        reasonable control, including but not limited to: acts of God, natural disasters,
        pandemics, war, terrorism, riots, government actions, power failures, internet or
        telecommunications failures, cyber-attacks, third-party service provider outages, or
        any other event outside our reasonable control (&quot;Force Majeure Event&quot;).
      </p>
      <p>
        In the event of a Force Majeure Event, our obligations under these Terms will be
        suspended for the duration of such event, and we will not be liable for any resulting
        loss or damage. We will use reasonable efforts to mitigate the impact of such events
        and resume normal operations as soon as practicable.
      </p>

      <h2 className="text-2xl font-semibold mt-12 mb-6 text-gray-800">
        15. Changes to Terms
      </h2>
      <p>
        We reserve the right, at our sole discretion, to modify or replace these Terms at any
        time. If a revision is material, we will try to provide at least 30 days&apos; notice
        prior to any new terms taking effect.
      </p>
      <p>
        By continuing to access or use our Service after those revisions become effective,
        you agree to be bound by the revised terms. If you do not agree to the new terms,
        please stop using the Service.
      </p>

      <h2 className="text-2xl font-semibold mt-12 mb-6 text-gray-800">
        16. Contact Us
      </h2>
      <p className="mb-4">
        If you have any questions about these Terms, please contact us:
      </p>
      <ul className="list-disc pl-6 mb-8">
        <li className="text-gray-700">
          By email:{' '}
          <a
            className="text-blue-600 hover:underline"
            href="mailto:devnicknish@gmail.com"
          >
            devnicknish@gmail.com
          </a>
        </li>
      </ul>
    </div>
  </div>
);
